// Recipient chips input with autocomplete from /api/contacts/suggest.
// Pills per address; Enter / comma / Tab commits the current text; Backspace
// on empty input removes the last chip; suggestions ranked by how often
// we've corresponded with the address.

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ContactSuggestion {
  email: string;
  name: string;
  timesSeen: number;
}

function isValidEmail(s: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.trim());
}

export function RecipientChips({
  value,
  onChange,
  placeholder,
  autoFocus,
  className,
}: {
  /** Committed addresses. */
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
}) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Debounced suggestion fetch.
  useEffect(() => {
    const q = text.trim();
    if (!q) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;
      fetch(`/api/contacts/suggest?q=${encodeURIComponent(q)}`, {
        credentials: 'include',
        signal: ctl.signal,
      })
        .then((r) => (r.ok ? r.json() : { contacts: [] }))
        .then((j: { contacts: ContactSuggestion[] }) => {
          const fresh = j.contacts.filter((c) => !value.includes(c.email));
          setSuggestions(fresh);
          setOpen(fresh.length > 0);
          setHighlight(0);
        })
        .catch(() => {});
    }, 150);
    return () => clearTimeout(t);
  }, [text, value]);

  function commit(addr: string) {
    const email = addr.trim().replace(/[,;]$/, '');
    if (!email) return;
    if (!isValidEmail(email)) return; // silently keep typing
    if (!value.includes(email.toLowerCase())) {
      onChange([...value, email.toLowerCase()]);
    }
    setText('');
    setSuggestions([]);
    setOpen(false);
  }

  function removeAt(i: number) {
    onChange(value.filter((_, j) => j !== i));
    inputRef.current?.focus();
  }

  return (
    <div className={cn('relative min-w-0 flex-1', className)}>
      <div
        className="flex min-h-[28px] w-full cursor-text flex-wrap items-center gap-1"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((addr, i) => (
          <span
            key={addr}
            className="inline-flex items-center gap-1 rounded-full border bg-secondary px-2 py-0.5 text-xs"
          >
            <span className="max-w-[200px] truncate">{addr}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                removeAt(i);
              }}
              className="rounded-full p-0.5 opacity-60 hover:opacity-100"
              aria-label={`Remove ${addr}`}
            >
              <X className="size-2.5" strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={text}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => {
            // Commit a valid dangling address on blur, then close the list
            // (deferred so a click on a suggestion lands first).
            setTimeout(() => {
              if (isValidEmail(text)) commit(text);
              setOpen(false);
            }, 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' && open) {
              e.preventDefault();
              setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
            } else if (e.key === 'ArrowUp' && open) {
              e.preventDefault();
              setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
              if (open && suggestions[highlight]) {
                e.preventDefault();
                commit(suggestions[highlight]!.email);
              } else if (text.trim()) {
                e.preventDefault();
                commit(text);
              }
            } else if (e.key === 'Backspace' && !text && value.length > 0) {
              removeAt(value.length - 1);
            } else if (e.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={value.length === 0 ? placeholder : undefined}
          className="min-w-[140px] flex-1 bg-transparent py-0.5 text-sm outline-none placeholder:text-muted-foreground/50"
        />
      </div>
      {open && (
        <ul className="absolute left-0 top-full z-50 mt-1 max-h-56 w-full min-w-[260px] overflow-auto rounded-md border bg-popover py-1 shadow-md">
          {suggestions.map((sg, i) => (
            <li key={sg.email}>
              <button
                type="button"
                // onMouseDown so the input's blur doesn't dismiss first.
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(sg.email);
                }}
                className={cn(
                  'flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-sm',
                  i === highlight ? 'bg-accent' : 'hover:bg-accent/50',
                )}
              >
                {sg.name && <span className="shrink-0 font-medium">{sg.name}</span>}
                <span className="min-w-0 truncate text-xs text-muted-foreground">{sg.email}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
